import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import { ComponentId } from 'pages/reporting/reports/rug/types'
import { useMemo } from 'react'
import { GeckoMeasurement, GeckoMeasurementRange } from 'types'

/**
 * Hook to get the nominal range string for components
 * @param componentToNominalRange Map of component IDs to their nominal ranges
 * @returns A formatted string representing the nominal range
 */
export const useComponentNominalRangeString = (
  componentToNominalRange: Map<ComponentId, GeckoMeasurementRange> | undefined
): string => {
  const { formatMeasurement } = useRugReportOps()

  return useMemo(() => {
    if (!componentToNominalRange || componentToNominalRange.size === 0) {
      return 'N/A'
    }

    const nominalThicknesses = Array.from(componentToNominalRange.values())
      .flatMap((range) => [range.min, range.max])
      .reduce<GeckoMeasurement[]>((uniqueNominals, nominal) => {
        if (
          uniqueNominals.some((existingNominal) =>
            GeckoMeasurement.Equivalent(existingNominal, nominal)
          )
        ) {
          return uniqueNominals
        }

        return [...uniqueNominals, nominal]
      }, [])
      .sort(
        (left, right) => left.toNumber(left.unit) - right.toNumber(left.unit)
      )

    const formatNominalThickness = (nominalThickness: GeckoMeasurement) =>
      formatMeasurement({
        measurement: nominalThickness,
        variant: 'narrow',
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })

    if (nominalThicknesses.length === 1) {
      return formatNominalThickness(nominalThicknesses[0]) ?? 'N/A'
    }

    return `${formatNominalThickness(
      nominalThicknesses[0]
    )} - ${formatNominalThickness(nominalThicknesses[nominalThicknesses.length - 1])}`
  }, [componentToNominalRange, formatMeasurement])
}
